import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    getLearningPosts,
    getLearningPostsByTags,
    getLearningTags
} from '../services/aprender';
import usePagination from './usePagination';

export const useLearningPosts = (scope = 'default') => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const handlerFetchData = async ({ queryKey }) => {
        const [, , { paginationModel }] = queryKey;

        return getLearningPosts(
            paginationModel.pageSize,
            currentToken(paginationModel.page)
        );
    };

    const { isPending, isError, data } = useQuery({
        queryKey: ['learning-posts', scope, { paginationModel }],
        queryFn: handlerFetchData,
        keepPreviousData: true
    });

    useEffect(() => {
        if (data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, paginationModel.page]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

const id_tag = 'learning-posts-by-tags';

export const useLearningPostsByTags = (tags = []) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const handlerFetchData = async ({ queryKey }) => {
        const [, { paginationModel, tags }] = queryKey;

        if (!tags.length) return { data: [], headers: {} };

        return getLearningPostsByTags(
            tags,
            paginationModel.pageSize,
            currentToken(paginationModel.page)
        );
    };

    const { isPending, isError, data } = useQuery({
        queryKey: [id_tag, { paginationModel, tags }],
        queryFn: handlerFetchData,
        enabled: tags.length > 0,
        keepPreviousData: true
    });

    useEffect(() => {
        if (data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

export const useLearningTags = () => {
    return useQuery({
        queryKey: ['learning-tags'],
        queryFn: getLearningTags
    });
};
